import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import Button from '../../components/Button';
import Table, { Column } from '../../components/Table';
import Modal from '../../components/Modal';
import Input from '../../components/Input';

const TABS = ['Initialize', 'Link Attendance Data', 'Salary Adjustment'] as const;

export default function PayrollCycle() {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const [tab, setTab] = useState<(typeof TABS)[number]>('Initialize');
  const [employees, setEmployees] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [linkOptions, setLinkOptions] = useState({
    includeLateness: true,
    includeAbsences: true,
    includeOt: true,
    offsetOtAgainstLateness: false,
    monthlyGraceNoPayHours: 0,
    enableOt72Cap: false,
  });
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [editForm, setEditForm] = useState<any>({});

  const loadEmployees = () => {
    setLoading(true);
    api.get(`/payroll/runs/${runId}/employees`).then((res) => setEmployees(res.data.employees)).finally(() => setLoading(false));
  };
  useEffect(loadEmployees, [runId]);

  const flash = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const initialize = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/payroll/runs/${runId}/initialize`, {});
      flash(`Initialized ${res.data.initialized} employee(s).`);
      loadEmployees();
    } catch (e: any) {
      flash(e.response?.data?.message || 'Failed to initialize');
    } finally {
      setLoading(false);
    }
  };

  const linkAttendance = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/payroll/runs/${runId}/link-attendance`, linkOptions);
      flash(`Linked attendance for ${res.data.linked} employee(s).`);
      loadEmployees();
    } catch (e: any) {
      flash(e.response?.data?.message || 'Failed to link attendance');
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (row: any) => {
    setEditTarget(row);
    setEditForm({
      basicSalary: row.basic_salary,
      incrementAmount: row.increment_amount,
      backPayAmount: row.back_pay_amount,
      otHours: row.ot_hours,
      otSource: row.ot_source,
      unpaidLeaveDays: row.unpaid_leave_days,
      absentDays: row.absent_days,
      latenessMinutes: row.lateness_minutes,
      restDayUnits: row.rest_day_units,
      phUnits: row.ph_units,
      bonusAmount: row.bonus_amount,
      nsPay: row.ns_pay,
      payslipMessage: row.payslip_message || '',
    });
  };

  const saveAdjustment = async () => {
    try {
      await api.put(`/payroll/runs/${runId}/employees/${editTarget.id}`, editForm);
      setEditTarget(null);
      flash('Salary adjustment saved and recalculated.');
      loadEmployees();
    } catch (e: any) {
      flash(e.response?.data?.message || 'Failed to save');
    }
  };

  const recalculateAll = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/payroll/runs/${runId}/recalculate`, {});
      flash(`Recalculated ${res.data.recalculated} employee(s).`);
      loadEmployees();
    } finally {
      setLoading(false);
    }
  };

  const update = async () => {
    setLoading(true);
    try {
      const res = await api.post(`/payroll/runs/${runId}/process`, {});
      flash(`Processed ${res.data.result.processed}/${res.data.result.totalEmployees} employee(s). Net pay total: ${res.data.result.totalNetPay}`);
      loadEmployees();
    } catch (e: any) {
      flash(e.response?.data?.message || 'Failed to process');
    } finally {
      setLoading(false);
    }
  };

  const columns: Column<any>[] = [
    { key: 'employee_code', header: 'Code', sortable: true },
    { key: 'first_name', header: 'Name', render: (r) => `${r.first_name} ${r.last_name || ''}` },
    { key: 'basic_salary', header: 'Basic' },
    { key: 'ot_hours', header: 'OT Hrs' },
    { key: 'ot_amount', header: 'OT Amt' },
    { key: 'unpaid_leave_days', header: 'Unpaid Leave' },
    { key: 'absent_days', header: 'Absent' },
    { key: 'lateness_minutes', header: 'Late (min)' },
    { key: 'gross_amount', header: 'Gross' },
    { key: 'cpf_employee_display', header: 'CPF (EE)' },
    { key: 'nett_amount', header: 'Nett' },
    { key: 'status', header: 'Status' },
    { key: 'actions', header: '', render: (r) => <Button variant="ghost" onClick={() => openEdit(r)}>Adjust</Button> },
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold text-gray-800">Payroll Cycle</h1>
        <Button variant="secondary" onClick={() => navigate('/payroll/payslips')}>
          Back to Runs
        </Button>
      </div>

      <div className="flex gap-2 mb-4 border-b">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {t}
          </button>
        ))}
      </div>
      {message && <div className="mb-3 text-sm text-green-700 bg-green-50 px-3 py-2 rounded">{message}</div>}

      {tab === 'Initialize' && (
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <p className="text-sm text-gray-600 mb-3">
            Snapshots basic salary and recurring allowances/deductions from each employee profile into this payroll period.
            Re-runnable per employee after profile changes.
          </p>
          <Button onClick={initialize} loading={loading}>
            Initialize (All Active Employees)
          </Button>
        </div>
      )}

      {tab === 'Link Attendance Data' && (
        <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-2">
          {[
            ['includeLateness', 'Include Lateness'],
            ['includeAbsences', 'Include Absences'],
            ['includeOt', 'Include OT'],
            ['offsetOtAgainstLateness', 'Offset OT against Lateness'],
            ['enableOt72Cap', 'Enable OT 72-hour Cap'],
          ].map(([key, label]) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={(linkOptions as any)[key]}
                onChange={(e) => setLinkOptions({ ...linkOptions, [key]: e.target.checked })}
              />
              {label}
            </label>
          ))}
          <Input
            label="Monthly Grace No-Pay Hours"
            type="number"
            value={linkOptions.monthlyGraceNoPayHours}
            onChange={(e) => setLinkOptions({ ...linkOptions, monthlyGraceNoPayHours: Number(e.target.value) })}
          />
          <Button onClick={linkAttendance} loading={loading}>
            Import
          </Button>
        </div>
      )}

      {tab === 'Salary Adjustment' && (
        <div className="flex gap-2 mb-4">
          <Button variant="secondary" onClick={recalculateAll} loading={loading}>
            Recalculate
          </Button>
          <Button onClick={update} loading={loading}>
            Update (Generate Payslips)
          </Button>
        </div>
      )}

      <Table columns={columns} rows={employees} loading={loading} />

      <Modal isOpen={!!editTarget} onClose={() => setEditTarget(null)} title={`Salary Adjustment — ${editTarget?.employee_code || ''}`}>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Basic Salary" type="number" value={editForm.basicSalary} onChange={(e) => setEditForm({ ...editForm, basicSalary: e.target.value })} />
          <Input label="Increment" type="number" value={editForm.incrementAmount} onChange={(e) => setEditForm({ ...editForm, incrementAmount: e.target.value })} />
          <Input label="Back Pay" type="number" value={editForm.backPayAmount} onChange={(e) => setEditForm({ ...editForm, backPayAmount: e.target.value })} />
          <Input label="Bonus" type="number" value={editForm.bonusAmount} onChange={(e) => setEditForm({ ...editForm, bonusAmount: e.target.value })} />
          <Input label="OT Hours" type="number" value={editForm.otHours} onChange={(e) => setEditForm({ ...editForm, otHours: e.target.value })} />
          <Input label="Unpaid Leave Days" type="number" value={editForm.unpaidLeaveDays} onChange={(e) => setEditForm({ ...editForm, unpaidLeaveDays: e.target.value })} />
          <Input label="Absent Days" type="number" value={editForm.absentDays} onChange={(e) => setEditForm({ ...editForm, absentDays: e.target.value })} />
          <Input label="Lateness (min)" type="number" value={editForm.latenessMinutes} onChange={(e) => setEditForm({ ...editForm, latenessMinutes: e.target.value })} />
          <Input label="Rest Day Units" type="number" value={editForm.restDayUnits} onChange={(e) => setEditForm({ ...editForm, restDayUnits: e.target.value })} />
          <Input label="PH Units" type="number" value={editForm.phUnits} onChange={(e) => setEditForm({ ...editForm, phUnits: e.target.value })} />
          <Input label="NS Pay" type="number" value={editForm.nsPay} onChange={(e) => setEditForm({ ...editForm, nsPay: e.target.value })} />
        </div>
        <Input
          label="Payslip Message"
          className="mt-3"
          value={editForm.payslipMessage}
          onChange={(e) => setEditForm({ ...editForm, payslipMessage: e.target.value })}
        />
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="secondary" onClick={() => setEditTarget(null)}>
            Cancel
          </Button>
          <Button onClick={saveAdjustment}>Save & Recalculate</Button>
        </div>
      </Modal>
    </div>
  );
}
